/**
 * v0.4.20 UI8 — Workflow step-completion derivation (pure logic).
 *
 * Operator sign-off 2026-02-15. All 11 Y/Ns + 2 amendments.
 *
 * Steps:
 *   1 Upload          — at least one ingest run SELECTED.
 *   2 Period+Seller   — period_start && period_end && start<=end &&
 *                       registrations.length>0 && seller_country in regs.
 *   3 Calculate       — calc.run_ids ⊇ selected && calc.period_*
 *                       matches form (auto-invalidates on edit).
 *   4 Review          — family_a_count - sum(acked) === 0 &&
 *                       family_b_count === 0 &&
 *                       replay_conflict_count === 0.
 *                       REPLAYED_FROM_PRIOR_CALC rows do NOT block.
 *   5 Sign-off        — calc.signed_off_at exists (append-only).
 *   6 Freeze+Export   — calc.frozen === true && filing_reference set.
 *
 * Tooltip dictionary returns the SPECIFIC blocker for each step
 * (operator spec: generic "complete previous steps" is forbidden).
 */

export const STEPS = ["upload", "configure", "calculate", "review", "signoff", "freeze"];

import { calcInputChanges, INPUT_CHANGE_TEXT } from "./calcInputs.js";
import { isSignedOff } from "./calcStatus.js";   // A2-581 lead 35

// A2-581 lead 30: said "Step 2 inputs" when the files (Step 1) or the client
// changed; the reasons listed under it name which input.
export const STALE_CALC_MESSAGE = "The inputs have changed since the last calc — re-run required";

export function deriveWorkflowState(input) {
  const {
    selectedRunIds = [],
    periodStart = "",
    periodEnd = "",
    registrations = [],
    sellerCountry = "",
    calc = null,
    review = null,            // /review-grouped response (may be null)
    acknowledgements = null,  // /acknowledgements list (may be null)
  } = input || {};

  // Step 1
  const step1Complete = selectedRunIds.length > 0;

  // Step 2 — tooltip amendment: include seller_country match.
  const sellerInRegs = !!sellerCountry && registrations.includes(sellerCountry.toUpperCase());
  const step2Reasons = [];
  if (!periodStart) step2Reasons.push("Period start required");
  if (!periodEnd) step2Reasons.push("Period end required");
  if (periodStart && periodEnd && periodEnd < periodStart)
    step2Reasons.push("Period end is before period start");
  if (registrations.length === 0)
    step2Reasons.push("Configure seller VAT registrations in Settings — calculations cannot run without them");
  else if (!sellerInRegs)
    step2Reasons.push("Seller country must match at least one configured VAT registration");

  // Step 3 — calc inputs must match current form (auto-invalidate).
  // A1-93: every recorded input (files, period, client, country filter,
  // FX) — see calcInputs.js. A mismatched calc is no longer dropped by
  // WorkflowContext; it stays loaded and is STALE: steps 3-6 incomplete,
  // sign-off and lock blocked, with the true reasons listed below.
  const inputChanges = input?.ignoreInputChanges ? [] : calcInputChanges(input);
  const calcMatchesInputs = !!calc && inputChanges.length === 0;
  const calcStale = !!calc && !calcMatchesInputs;
  const staleReasons = inputChanges.map((k) => INPUT_CHANGE_TEXT[k]);
  // v0.5.7 — A calc whose run_ids + period match the current form is itself
  // proof that the upstream config steps (1-3) were satisfied WHEN it was
  // produced. Downstream review / sign-off / freeze must NOT be re-gated on
  // live, mutable upstream config (e.g. seller registrations that are
  // client-scoped or changed since the calc ran) — the auto-invalidate
  // cascade in WorkflowContext already drops the calc if run_ids/period
  // move. Without this, a loaded, reconciled, fully-reviewed calc showed
  // "Ready to file" while the Continue-to-sign-off button stayed disabled
  // because /api/settings had no registrations — a direct contradiction of
  // the single filing-readiness verdict (ARCHITECTURE_INVARIANTS #4).
  const step2Complete = calcMatchesInputs
    || (step1Complete && step2Reasons.length === 0);
  const step3Reasons = [];
  if (!calcMatchesInputs) {
    // A1-93 — the stale calc is named first, whatever else Step 2 lacks
    // (with the calc no longer matching, Step 2 is judged on live inputs).
    if (calc) step3Reasons.push(STALE_CALC_MESSAGE, ...staleReasons);
    if (!step2Complete) step3Reasons.push(...step2Reasons);
  }
  const step3Complete = step2Complete && calcMatchesInputs;

  // Step 4 — review queue clear (Family A acked, Family B + conflicts resolved).
  // A1-103 (operator 2026-10-31): information cards (tier "info") are never a
  // duty — only Family A cards that can change a figure must be answered.
  const familyA = (review?.groups || []).some((g) => g.tier)
    ? (review.groups || []).filter((g) => g.family === "A" && g.tier !== "info")
        .reduce((s, g) => s + (g.count || 0), 0)
    : (review?.family_a_count || 0);
  const familyB = review?.family_b_count || 0;
  // REPLAY_CONFLICT rides inside Family B groups already (per backend
  // taxonomy). Replayed-no-conflict rows live in the synthetic group
  // and do not contribute to either count, so they don't block.
  const ackedFamilyA = (review?.groups || [])
    .filter((g) => g.family === "A" && g.tier !== "info")
    .reduce((s, g) => s + (g.acknowledged_count || 0), 0);
  const familyAOpen = Math.max(0, familyA - ackedFamilyA);
  // v0.5.0i+6 — symmetric treatment for Family B. Per-row acks
  // (acknowledged_count populated by /review-grouped) subtract
  // from the family_b_count gate, so a Family B row that has
  // been explicitly resolved by the operator stops blocking
  // Step 4 the same way an acked Family A row does. Without
  // this, Family B rows are unresolvable from the UI and the
  // step stays permanently locked — operator-reported on
  // 2026-06-23 with a real UNCLASSIFIED row in production.
  const ackedFamilyB = (review?.groups || [])
    .filter((g) => g.family === "B")
    .reduce((s, g) => s + (g.acknowledged_count || 0), 0);
  const familyBOpen = Math.max(0, familyB - ackedFamilyB);
  const replayConflictCount = (review?.groups || [])
    .filter((g) => g.flag_type === "REPLAY_CONFLICT")
    .reduce((s, g) => s + (g.count || 0), 0);
  const step4Reasons = [];
  if (familyBOpen > 0)
    step4Reasons.push(`${familyBOpen} Family B flag${familyBOpen === 1 ? "" : "s"} require per-row decisions`);
  if (familyAOpen > 0)
    step4Reasons.push(`${familyAOpen} Family A flag${familyAOpen === 1 ? "" : "s"} awaiting acknowledgement`);
  if (replayConflictCount > 0)
    step4Reasons.push(`${replayConflictCount} replay conflict${replayConflictCount === 1 ? "" : "s"} require manual resolution`);
  const step4Complete = step3Complete && step4Reasons.length === 0;
  // v0.5.7 — never present a disabled "Continue to sign-off" button with an
  // empty tooltip: when step 4 is held incomplete solely by an upstream
  // step, surface that reason instead of an empty blocker list.
  const step4Blockers = step4Reasons.length > 0
    ? step4Reasons
    : (!step3Complete
        ? (step3Reasons.length
            ? step3Reasons
            // A2-581 lead 2: "Re-run" said nothing was ever run.
            : [calc ? "Re-run the calculation for the current period before continuing"
                    : "Run the calculation in Step 3 before continuing"])
        : []);

  // Step 5 — sign-off append-only.
  const signedOff = isSignedOff(calc);
  const step5Reasons = [];
  if (!step4Complete) {
    // A1-93 — a stale calc is never signed: the re-run is the blocker,
    // and it used to produce an EMPTY reason here (step4Reasons only
    // carries review flags), so sign-off was disabled with no tooltip.
    if (calcStale)
      step5Reasons.push(...step3Reasons);
    else if (familyB + familyAOpen + replayConflictCount > 0)
      step5Reasons.push(`${familyB + familyAOpen + replayConflictCount} flagged rows still in review — return to Step 4 to resolve them before signing off`);
    else
      step5Reasons.push(...step4Reasons);
  }
  const step5Complete = step4Complete && signedOff;

  // Step 6 — freeze + filing reference.
  const frozen = !!(calc && calc.frozen);
  const filingRef = !!(calc && calc.filing_reference);
  const step6Reasons = [];
  if (!step5Complete) {
    // v0.6.8 fix — this used to always say "Operator sign-off required",
    // even once the operator HAD already signed off and step5Complete
    // was false only because step4's review flags weren't clear (e.g. a
    // pending rate-drift ack). That told a signed-off operator to go
    // back and do something they'd already done, with no hint that the
    // real blocker was an unresolved review row — operator-reported
    // 2026-09-04 ("no error message nothing, I cant be sitting and
    // guessing"). Surface the actual step4/5 blocker when already
    // signed off; keep the sign-off prompt only when that's the real gap.
    step6Reasons.push(
      signedOff
        ? (step5Reasons[0] || "Return to Step 4 — outstanding review flags are blocking the freeze")
        : "Operator sign-off required — return to Step 5 and sign off as the filing operator",
    );
  }
  const step6Complete = step5Complete && frozen && filingRef;

  // Active step = first incomplete; if all done, sit on Step 6.
  const completeFlags = [step1Complete, step2Complete, step3Complete, step4Complete, step5Complete, step6Complete];
  const firstIncomplete = completeFlags.findIndex((c) => !c);
  const activeStep = firstIncomplete === -1 ? 5 : firstIncomplete;
  // A1-93 — how far the stale calc had got, so its screens stay
  // viewable (marked stale) instead of vanishing.
  const staleReach = calcStale
    ? deriveWorkflowState({ ...input, ignoreInputChanges: true }).activeStep : 0;

  // A2-581 lead 1: a step held back only by an earlier step had an EMPTY
  // list, so clicking it in the stepper did nothing. It now names the
  // earlier step's reason.
  const steps = {
    1: { complete: step1Complete, blockers: step1Complete ? [] : ["Select at least one ingest run from the production files list to continue"] },
    2: { complete: step2Complete, blockers: step2Reasons },
    3: { complete: step3Complete, blockers: step3Reasons },
    4: { complete: step4Complete, blockers: step4Blockers },
    5: { complete: step5Complete, blockers: step5Reasons },
    6: { complete: step6Complete, blockers: step6Reasons },
  };
  for (let n = 2; n <= 6; n++) {
    if (!steps[n].complete && !steps[n].blockers.length && !steps[n - 1].complete)
      steps[n] = { ...steps[n], blockers: steps[n - 1].blockers };
  }

  return {
    steps,
    activeStep: activeStep + 1, // 1-indexed for UI
    counters: {
      familyAOpen, familyB, familyBOpen, replayConflictCount,
    },
    derived: {
      sellerInRegs, calcMatchesInputs, signedOff, frozen, filingRef,
      calcStale, staleReasons, staleReach, staleInputs: inputChanges,
    },
  };
}

export const exportAllowed = (state) =>
  state?.steps?.[6]?.complete === true ||
  // Allow export on a frozen calc even if the active step jumped ahead
  // somehow (defensive: never allow on unfrozen).
  (state?.derived?.frozen && state?.derived?.filingRef);

// Tooltip for the Export XLSX button.
export const exportTooltip = () =>
  "Export available after freeze — assigns an immutable filing reference to the XLSX.";
